/**
 * Event Delegation Utilities
 *
 * Namespaced helpers for managing event listeners with HTMX compatibility.
 * Uses event delegation with closest() to work with dynamically loaded content.
 */

export const Events = {
    /**
     * Register event handler using data attribute selector
     * Compatible with HTMX dynamic content
     *
     * @param {string} eventType - DOM event type (click, input, change, etc.)
     * @param {string} dataAttr - Data attribute name (without 'data-' prefix)
     * @param {(event: Event, element: HTMLElement) => void} handler - Event handler function
     *
     * @example
     * Events.on('click', 'task-toggle', (e, element) => {
     *     console.log('Clicked element:', element);
     * });
     */
    on(eventType, dataAttr, handler) {
        document.addEventListener(eventType, (e) => {
            // Not every event target is an Element — one dispatched on document
            // or window has no closest(), and this runs before the try below.
            if (!(e.target instanceof Element)) return;
            const element = e.target.closest(`[data-${dataAttr}]`);
            if (element) {
                try {
                    handler(e, element);
                } catch (error) {
                    console.error(`Error in ${eventType} handler for [data-${dataAttr}]:`, error);
                }
            }
        }, true); // Use capture phase for better performance
    },

    /**
     * Execute callback when DOM is ready
     * Handles both early and late script loading
     *
     * @param {() => void} callback - Function to execute when DOM is ready
     *
     * @example
     * Events.onReady(() => {
     *     console.log('DOM is ready!');
     * });
     */
    onReady(callback) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', callback);
        } else {
            callback();
        }
    },

    /**
     * Register multiple event handlers at once
     *
     * @param {Array<{type: string, attr: string, handler: Function}>} handlers - Array of handler configs
     *
     * @example
     * Events.onAll([
     *     { type: 'click', attr: 'task-toggle', handler: toggleTask },
     *     { type: 'input', attr: 'task-search', handler: searchTasks }
     * ]);
     */
    onAll(handlers) {
        handlers.forEach(({ type, attr, handler }) => {
            this.on(type, attr, handler);
        });
    }
};
